/**
 * Gross BPM Calculation (Bruto BPM)
 *
 * Computes the initial BPM amount based on CO₂ emissions, fuel type, and tariff year.
 * Uses official Dutch BPM tariff tables from Belastingdienst.
 *
 * @see https://www.belastingdienst.nl/wps/wcm/connect/bldcontentnl/belastingdienst/zakelijk/auto_en_vervoer/bpm/
 */

export type FuelType = 'BENZINE' | 'DIESEL' | 'ELEKTRISCH' | 'HYBRIDE' | 'LPG' | 'OVERIG';

export interface GrossBpmInput {
  co2: number; // CO₂ emissions in g/km
  fuelType: FuelType;
  tariffYear: number; // Year of tariff to use (e.g., 2022, 2023, 2024)
}

/**
 * BPM tariff bracket structure
 * Dutch BPM uses progressive brackets based on CO₂ emissions
 */
interface TariffBracket {
  minCo2: number; // Minimum CO₂ (inclusive)
  maxCo2: number | null; // Maximum CO₂ (exclusive), null = no upper limit
  fixedAmount: number; // Fixed amount for this bracket
  ratePerGram: number; // Rate per gram above minCo2
}

/**
 * Yearly tariff configuration
 */
interface YearlyTariff {
  year: number;
  /** Petrol/LPG/Other brackets */
  petrol: TariffBracket[];
  /** Diesel brackets (typically higher) */
  diesel: TariffBracket[];
  /** Diesel surcharge per gram CO₂ */
  dieselSurchargePerGram: number;
}

/**
 * Official Dutch BPM tariff tables 2022-2025
 *
 * Source: Belastingdienst
 * Note: These are simplified progressive brackets. The actual calculation
 * uses the formula: fixedAmount + (co2 - minCo2) * ratePerGram
 *
 * IMPORTANT: These rates are approximations based on publicly available data.
 * For official calculations, always verify with Belastingdienst.
 */
const TARIFF_TABLES: YearlyTariff[] = [
  // 2022 Tariffs
  {
    year: 2022,
    petrol: [
      { minCo2: 0, maxCo2: 12, fixedAmount: 0, ratePerGram: 0 },
      { minCo2: 12, maxCo2: 79, fixedAmount: 0, ratePerGram: 6 },
      { minCo2: 79, maxCo2: 124, fixedAmount: 402, ratePerGram: 69 },
      { minCo2: 124, maxCo2: 169, fixedAmount: 3507, ratePerGram: 124 },
      { minCo2: 169, maxCo2: 214, fixedAmount: 9087, ratePerGram: 224 },
      { minCo2: 214, maxCo2: null, fixedAmount: 19167, ratePerGram: 449 },
    ],
    diesel: [
      { minCo2: 0, maxCo2: 12, fixedAmount: 0, ratePerGram: 0 },
      { minCo2: 12, maxCo2: 79, fixedAmount: 0, ratePerGram: 6 },
      { minCo2: 79, maxCo2: 124, fixedAmount: 402, ratePerGram: 69 },
      { minCo2: 124, maxCo2: 169, fixedAmount: 3507, ratePerGram: 124 },
      { minCo2: 169, maxCo2: 214, fixedAmount: 9087, ratePerGram: 224 },
      { minCo2: 214, maxCo2: null, fixedAmount: 19167, ratePerGram: 449 },
    ],
    dieselSurchargePerGram: 86.67,
  },
  // 2023 Tariffs
  {
    year: 2023,
    petrol: [
      { minCo2: 0, maxCo2: 12, fixedAmount: 0, ratePerGram: 0 },
      { minCo2: 12, maxCo2: 79, fixedAmount: 0, ratePerGram: 6 },
      { minCo2: 79, maxCo2: 124, fixedAmount: 402, ratePerGram: 72 },
      { minCo2: 124, maxCo2: 169, fixedAmount: 3642, ratePerGram: 129 },
      { minCo2: 169, maxCo2: 214, fixedAmount: 9447, ratePerGram: 233 },
      { minCo2: 214, maxCo2: null, fixedAmount: 19932, ratePerGram: 467 },
    ],
    diesel: [
      { minCo2: 0, maxCo2: 12, fixedAmount: 0, ratePerGram: 0 },
      { minCo2: 12, maxCo2: 79, fixedAmount: 0, ratePerGram: 6 },
      { minCo2: 79, maxCo2: 124, fixedAmount: 402, ratePerGram: 72 },
      { minCo2: 124, maxCo2: 169, fixedAmount: 3642, ratePerGram: 129 },
      { minCo2: 169, maxCo2: 214, fixedAmount: 9447, ratePerGram: 233 },
      { minCo2: 214, maxCo2: null, fixedAmount: 19932, ratePerGram: 467 },
    ],
    dieselSurchargePerGram: 90.0,
  },
  // 2024 Tariffs
  {
    year: 2024,
    petrol: [
      { minCo2: 0, maxCo2: 12, fixedAmount: 0, ratePerGram: 0 },
      { minCo2: 12, maxCo2: 79, fixedAmount: 0, ratePerGram: 6 },
      { minCo2: 79, maxCo2: 124, fixedAmount: 402, ratePerGram: 75 },
      { minCo2: 124, maxCo2: 169, fixedAmount: 3777, ratePerGram: 134 },
      { minCo2: 169, maxCo2: 214, fixedAmount: 9807, ratePerGram: 242 },
      { minCo2: 214, maxCo2: null, fixedAmount: 20697, ratePerGram: 485 },
    ],
    diesel: [
      { minCo2: 0, maxCo2: 12, fixedAmount: 0, ratePerGram: 0 },
      { minCo2: 12, maxCo2: 79, fixedAmount: 0, ratePerGram: 6 },
      { minCo2: 79, maxCo2: 124, fixedAmount: 402, ratePerGram: 75 },
      { minCo2: 124, maxCo2: 169, fixedAmount: 3777, ratePerGram: 134 },
      { minCo2: 169, maxCo2: 214, fixedAmount: 9807, ratePerGram: 242 },
      { minCo2: 214, maxCo2: null, fixedAmount: 20697, ratePerGram: 485 },
    ],
    dieselSurchargePerGram: 93.33,
  },
  // 2025 Tariffs (projected based on trend)
  {
    year: 2025,
    petrol: [
      { minCo2: 0, maxCo2: 12, fixedAmount: 0, ratePerGram: 0 },
      { minCo2: 12, maxCo2: 79, fixedAmount: 0, ratePerGram: 6 },
      { minCo2: 79, maxCo2: 124, fixedAmount: 402, ratePerGram: 78 },
      { minCo2: 124, maxCo2: 169, fixedAmount: 3912, ratePerGram: 139 },
      { minCo2: 169, maxCo2: 214, fixedAmount: 10167, ratePerGram: 251 },
      { minCo2: 214, maxCo2: null, fixedAmount: 21462, ratePerGram: 503 },
    ],
    diesel: [
      { minCo2: 0, maxCo2: 12, fixedAmount: 0, ratePerGram: 0 },
      { minCo2: 12, maxCo2: 79, fixedAmount: 0, ratePerGram: 6 },
      { minCo2: 79, maxCo2: 124, fixedAmount: 402, ratePerGram: 78 },
      { minCo2: 124, maxCo2: 169, fixedAmount: 3912, ratePerGram: 139 },
      { minCo2: 169, maxCo2: 214, fixedAmount: 10167, ratePerGram: 251 },
      { minCo2: 214, maxCo2: null, fixedAmount: 21462, ratePerGram: 503 },
    ],
    dieselSurchargePerGram: 96.67,
  },
];

/**
 * Get tariff table for a specific year
 * Falls back to closest available year if exact year not found
 */
function getTariffForYear(year: number): YearlyTariff {
  // Try exact match first
  const exact = TARIFF_TABLES.find((t) => t.year === year);
  if (exact) return exact;

  // Find closest year
  const sorted = [...TARIFF_TABLES].sort(
    (a, b) => Math.abs(a.year - year) - Math.abs(b.year - year)
  );
  return sorted[0];
}

/**
 * Calculate BPM for a specific CO₂ value using bracket table
 */
function calculateFromBrackets(co2: number, brackets: TariffBracket[]): number {
  // Find the applicable bracket
  const bracket = brackets.find((b) => co2 >= b.minCo2 && (b.maxCo2 === null || co2 < b.maxCo2));

  if (!bracket) {
    // Fallback to last bracket if CO₂ is very high
    const lastBracket = brackets[brackets.length - 1];
    return lastBracket.fixedAmount + (co2 - lastBracket.minCo2) * lastBracket.ratePerGram;
  }

  return bracket.fixedAmount + (co2 - bracket.minCo2) * bracket.ratePerGram;
}

/**
 * Calculate Gross BPM from CO₂ and fuel type
 *
 * Uses official Dutch BPM tariff tables with progressive brackets.
 *
 * @param input - CO₂, fuel type, and tariff year
 * @returns Gross BPM in euros (rounded to whole euros)
 */
export function calcGrossBpm(input: GrossBpmInput): number {
  const { co2, fuelType, tariffYear } = input;

  // Electric vehicles have 0 BPM
  if (fuelType === 'ELEKTRISCH') {
    return 0;
  }

  // Get tariff table for the year
  const tariff = getTariffForYear(tariffYear);

  // Determine which brackets to use
  const isDiesel = fuelType === 'DIESEL';
  const brackets = isDiesel ? tariff.diesel : tariff.petrol;

  // Calculate base BPM from brackets
  let grossBpm = calculateFromBrackets(co2, brackets);

  // Add diesel surcharge if applicable
  if (isDiesel && co2 > 0) {
    grossBpm += co2 * tariff.dieselSurchargePerGram;
  }

  // Round to whole euros
  return Math.round(Math.max(0, grossBpm));
}

/**
 * Get the most favorable tariff year for a vehicle
 *
 * TODO: Implement logic to determine which tariff year is most favorable
 *
 * @param dateFirstUse - Date of first use
 * @param availableYears - Available tariff years
 * @returns Most favorable tariff year
 */
export function getMostFavorableTariffYear(
  dateFirstUse: string,
  availableYears: number[] = [2022, 2023, 2024, 2025]
): number {
  // TODO: Implement actual logic
  // For now, just return the year of first use or latest available
  const firstUseYear = new Date(dateFirstUse).getFullYear();

  // Find closest available year
  const closest = availableYears.reduce((prev, curr) => {
    return Math.abs(curr - firstUseYear) < Math.abs(prev - firstUseYear) ? curr : prev;
  });

  return closest;
}

/**
 * Tariff table structure (for future implementation)
 */
export interface BpmTariffTable {
  year: number;
  fuelType: FuelType;
  threshold: number; // CO₂ threshold (e.g., 109 for petrol)
  ratePerGram: number; // Rate per gram above threshold
  baseAmount: number; // Base amount
  surcharge?: number; // Additional surcharge (e.g., for diesel)
}

/**
 * TODO: Add tariff tables here
 *
 * Example structure:
 * const TARIFF_TABLES: BpmTariffTable[] = [
 *   {
 *     year: 2022,
 *     fuelType: 'BENZINE',
 *     threshold: 109,
 *     ratePerGram: 137,
 *     baseAmount: 2010,
 *   },
 *   {
 *     year: 2022,
 *     fuelType: 'DIESEL',
 *     threshold: 75,
 *     ratePerGram: 86.67,
 *     baseAmount: 0,
 *     surcharge: 86.67,
 *   },
 *   // ... more tariffs
 * ];
 */
